import { useParams, Navigate, useLocation, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { slugify } from '../utils/cityServiceAreaData';
import { Button } from '../utils/Button';
import Breadcrumbs from '../components/Breadcrumbs';
import { FaMapMarkerAlt, FaTools, FaHome } from 'react-icons/fa';

const countyDescriptions = {
  'Box Elder': {
    description:
      "Box Elder County is home to diverse communities from Brigham City to Tremonton, each with its unique charm and character. At Oak and Stone Remodel, we've been transforming homes throughout Box Elder County with custom kitchen and bathroom renovations that blend modern functionality with timeless style. Our deep understanding of local architectural styles and homeowner preferences allows us to create spaces that perfectly match each community's character.",
    landmarks:
      'Notable landmarks include the historic Box Elder Tabernacle, Golden Spike National Historic Site, Bear River Migratory Bird Refuge, and Willard Bay State Park.',
    specialties:
      'We specialize in farmhouse-style kitchens, modern bathroom renovations, custom cabinetry, and complete home remodels. Our team is experienced in working with both historic homes and new constructions throughout Box Elder County.'
  },
  Cache: {
    description:
      "Cache County, with its beautiful mountain valley setting and historic communities like Logan and Providence, presents unique opportunities for home remodeling. Oak and Stone Remodel brings expertise in creating spaces that complement Cache Valley's distinctive architecture while incorporating modern amenities and design trends.",
    landmarks:
      'Key landmarks include Utah State University, Logan Temple, Old Main Hill, and the American West Heritage Center.',
    specialties:
      'Our Cache County projects often feature mountain-modern designs, traditional craftsman styles, and innovative space solutions for historic homes. We excel in kitchen remodels, custom bathrooms, and period-appropriate renovations.'
  },
  Weber: {
    description:
      'Weber County combines urban sophistication with suburban comfort, from historic Ogden to growing communities like North Ogden and Pleasant View. Oak and Stone Remodel has extensive experience transforming Weber County homes with renovations that respect architectural heritage while embracing modern living standards.',
    landmarks:
      'Significant landmarks include Historic 25th Street, Ogden Temple, Weber State University, and the Ogden Nature Center.',
    specialties:
      'We specialize in urban-modern kitchen designs, luxury bathroom renovations, and custom storage solutions. Our team has particular expertise in updating historic Ogden homes while preserving their character.'
  },
  Davis: {
    description:
      "Davis County's rapidly growing communities from Layton to Farmington demand sophisticated home renovations that combine style with functionality. Oak and Stone Remodel delivers premium remodeling services that enhance both the value and livability of Davis County homes.",
    landmarks:
      'Notable areas include Antelope Island State Park, Lagoon Amusement Park, Hill Air Force Base, and the Legacy Parkway Trail.',
    specialties:
      'Our Davis County expertise includes contemporary kitchen designs, spa-like bathroom renovations, and custom solutions for growing families. We excel in creating open-concept living spaces and multi-functional rooms.'
  },
  Rich: {
    description:
      'Rich County, with its stunning Bear Lake views and mountain setting, requires unique approaches to home remodeling. Oak and Stone Remodel creates custom solutions that maximize both the functionality and views in Rich County homes, perfect for both year-round residents and vacation properties.',
    landmarks:
      'Key features include Bear Lake, Rendezvous Beach State Park, and the Rich County Courthouse in Randolph.',
    specialties:
      'We specialize in lake house renovations, vacation home upgrades, and all-season living spaces. Our designs often incorporate large windows and open layouts to capture lake and mountain views.'
  }
};

export default function CountyServiceArea() {
  const { county } = useParams();
  const location = useLocation();
  const [countyData, setCountyData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch('/updatedData.JSON')
      .then((res) => res.json())
      .then((data) => {
        const arr = Array.isArray(data) ? data : data.default;
        // Get all cities for this county
        const countyEntries = arr.filter(
          (entry) => slugify(entry.County) === county
        );

        if (countyEntries.length > 0) {
          // Get the county name from the first entry
          const countyName = countyEntries[0].County;

          // Create county data object
          setCountyData({
            name: countyName,
            cities: countyEntries.map((entry) => ({
              name: entry.City,
              description: entry.Description,
              zipCode: entry['ZIP Code'],
              specialties: entry.Specialties
            })),
            ...countyDescriptions[countyName.replace(' County', '')]
          });
          setNotFound(false);
        } else {
          setNotFound(true);
        }
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setNotFound(true);
      });
  }, [county]);

  if (notFound) {
    return <Navigate to="/service-area" replace />;
  }

  if (!countyData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse flex flex-col items-center">
          <div className="h-8 w-32 bg-gray-200 rounded mb-4"></div>
          <div className="text-gray-500">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{`${countyData.name} Home Remodeling Services | Oak and Stone Remodel`}</title>
        <meta
          name="description"
          content={`Expert kitchen and bathroom remodeling services in ${
            countyData.name
          }, Utah. Serving ${countyData.cities
            .map((c) => c.name)
            .join(', ')}. Custom cabinets, renovations, and more.`}
        />
      </Helmet>

      {/* Hero Section with Breadcrumbs */}
      <div className="relative h-[50vh] w-full flex items-center justify-center">
        <div
          className="absolute inset-0 bg-center bg-cover"
          style={{
            backgroundImage: `url('https://res.cloudinary.com/dwoubdppz/image/upload/w_1200,q_auto,f_auto/v1751597871/oak-and-stone-remodel/show/show18.webp')`
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/60" />
        <div className="relative z-10 px-4 text-center text-white max-w-4xl mx-auto">
          <h1 className="mb-3 text-4xl font-bold tracking-wide uppercase drop-shadow-lg md:text-6xl">
            {countyData.name}
          </h1>
          <h2 className="mb-6 text-xl font-medium drop-shadow md:text-3xl">
            Expert Home Remodeling Services
          </h2>
          <Button
            to="/contact"
            text="Get Your Free Consultation"
            classname="text-white bg-primary hover:bg-primary-hover transform hover:scale-105 transition-all duration-300"
          />
        </div>
        <Breadcrumbs pathname={location.pathname} />
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-16">
        {/* Introduction Section */}
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Home Remodeling in {countyData.name}
          </h2>
          <p className="text-xl leading-relaxed text-gray-700 mb-8 max-w-4xl mx-auto">
            {countyData.description}
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-gray-50 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <FaMapMarkerAlt className="w-8 h-8 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Local Landmarks
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {countyData.landmarks}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <FaTools className="w-8 h-8 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Our Specialties
            </h3>
            <p className="text-gray-700 leading-relaxed">
              {countyData.specialties}
            </p>
          </div>
          <div className="bg-gray-50 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow">
            <FaHome className="w-8 h-8 text-blue-600 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-3">
              Service Area
            </h3>
            <p className="text-gray-700 leading-relaxed">
              We proudly serve all communities in {countyData.name}, providing
              expert remodeling services within a 20-mile radius of each city
              center.
            </p>
          </div>
        </div>

        {/* Cities Grid */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
            Communities We Serve in {countyData.name}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {countyData.cities.map((city) => (
              <Link
                key={city.name}
                to={`/service-area/${county}/${slugify(city.name)}`}
                className="group bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <FaMapMarkerAlt className="w-5 h-5 text-blue-600 mr-2" />
                    <h3 className="text-xl font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                      {city.name}
                    </h3>
                  </div>
                  <p className="text-gray-600 line-clamp-3 mb-4">
                    {city.description}
                  </p>
                  <span className="text-blue-600 font-medium group-hover:text-blue-800 transition-colors">
                    Learn more →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Ready to Transform Your Home?
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Contact us today to schedule your free consultation and discover how
            we can help bring your remodeling vision to life.
          </p>
          <Button
            to="/contact"
            text="Schedule Your Free Consultation"
            classname="text-white bg-primary hover:bg-primary-hover transform hover:scale-105 transition-all duration-300"
          />
        </div>
      </div>
    </div>
  );
}
